/**
 * Goal Game — operator console. Phase 0 demo, reached by a private token link.
 * One big GOAL! button, the window length, and End event.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

const API = `${(typeof window !== 'undefined' ? window.location.origin : '')}/api/goal-game`;

export default function GoalOperator() {
    const { token } = useParams();
    const { t, i18n } = useTranslation();
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const [window_, setWindow_] = useState(120);

    const load = useCallback(async () => {
        try {
            const res = await fetch(`${API}/operator/${token}`);
            if (res.status === 404) { setNotFound(true); return; }
            const json = await res.json();
            setData(json);
            setWindow_((prev) => (prev === json.window_seconds ? prev : json.window_seconds));
        } catch (_e) { /* next poll retries */ }
    }, [token]);

    useEffect(() => {
        load();
        const id = setInterval(load, 2000);
        return () => clearInterval(id);
    }, [load]);

    const post = async (path) => {
        setBusy(true);
        try {
            await fetch(`${API}/operator/${token}${path}`, { method: 'POST' });
            await load();
        } finally { setBusy(false); }
    };

    const saveWindow = async (value) => {
        setWindow_(value);
        await fetch(`${API}/operator/${token}/window`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ window_seconds: Number(value) }),
        });
        load();
    };

    if (notFound) {
        return (
            <div className="min-h-screen bg-[#0b1020] text-white flex items-center justify-center" data-testid="goal-operator-404">
                <p className="text-white/60">{t('goalGame.linkInvalid')}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-[#0b1020] flex items-center justify-center">
                <Loader2 className="animate-spin text-white/40" size={28} />
            </div>
        );
    }

    const live = data.event?.status === 'open';
    // At the 10-minute ceiling the granted window already equals the cap.
    const atCap = live && (data.event.window_seconds >= data.window_max_total);
    const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
    // Org colours (voucher_bg / voucher_text / voucher_accent). Unset → today's look.
    const c = {
        bg: data.brand?.colors?.bg || '#0b1020',
        text: data.brand?.colors?.text || '#ffffff',
        accent: data.brand?.colors?.accent || '#fbbf24',
    };

    return (
        <div className="min-h-screen px-6 py-10" style={{ backgroundColor: c.bg, color: c.text }}
             data-testid="goal-operator-page">
            <div className="max-w-xl mx-auto">
                <div className="flex gap-1 justify-end mb-6">
                    {['en', 'fr'].map((code) => (
                        <button
                            key={code} onClick={() => i18n.changeLanguage(code)}
                            data-testid={`goal-op-lang-${code}`}
                            style={lang === code ? { backgroundColor: c.accent, color: c.bg } : undefined}
                            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider ${
                                lang === code ? '' : 'bg-white/10 text-white/60'}`}
                        >{code}</button>
                    ))}
                </div>

                <p className="text-xs uppercase tracking-[0.2em] text-white/40">{t('goalGame.operatorTitle')}</p>
                {data.brand?.logo_url && (
                    <img
                        src={data.brand.logo_url.startsWith('http') ? data.brand.logo_url : `${(typeof window !== 'undefined' ? window.location.origin : '')}${data.brand.logo_url}`}
                        alt={data.organization_name || ''}
                        style={{ height: `${data.brand.logo_size_px || 48}px` }}
                        className="object-contain my-3"
                        data-testid="goal-operator-logo"
                    />
                )}
                <h1 className="text-3xl font-black tracking-tight mb-1" data-testid="goal-operator-org">{data.organization_name}</h1>
                <p className="text-sm text-white/50 mb-8">
                    {t('goalGame.operatorCard')}: <span className="font-mono">{data.card_id}</span> · {t('goalGame.odds', { n: data.odds_one_in })}
                </p>

                <button
                    onClick={() => post('/goal')}
                    disabled={busy}
                    data-testid="goal-fire-button"
                    style={{ backgroundColor: c.accent, color: c.bg }}
                    className="w-full rounded-3xl text-5xl font-black py-14 tracking-tight active:scale-[0.99] transition-transform disabled:opacity-60"
                >
                    {t('goalGame.goalButton')}
                </button>

                <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5" data-testid="goal-operator-status">
                    {live ? (
                        <>
                            <p className="text-sm text-white/60">{t('goalGame.windowOpen')}</p>
                            <p className="text-4xl font-black tabular-nums" data-testid="goal-operator-countdown">
                                {data.event.seconds_left}
                            </p>
                            <p className="text-xs uppercase tracking-widest text-white/40">{t('goalGame.secondsLeft')}</p>
                        </>
                    ) : (
                        <p className="text-sm text-white/60" data-testid="goal-operator-idle">{t('goalGame.windowIdle')}</p>
                    )}
                    <p className="mt-3 text-sm text-white/50" data-testid="goal-operator-counts">
                        {t('goalGame.plays')}: {data.counts.plays} · {t('goalGame.wins')}: {data.counts.wins}
                    </p>
                </div>

                <button
                    onClick={() => post('/extend')}
                    disabled={busy || !live || atCap}
                    data-testid="goal-extend-button"
                    className="mt-4 w-full rounded-xl bg-white/10 border border-white/20 py-4 text-lg font-bold text-white disabled:opacity-40"
                >
                    {t('goalGame.extendButton')}
                </button>
                <p className="mt-2 text-xs text-white/40" data-testid="goal-extend-note">
                    {atCap
                        ? t('goalGame.extendCapped', { n: Math.round(data.window_max_total / 60) })
                        : t('goalGame.extendHint', { n: Math.round(data.window_max_total / 60) })}
                </p>

                <div className="mt-6 rounded-2xl bg-white/5 border border-white/10 p-5">
                    <label className="block text-sm text-white/60 mb-2" htmlFor="goal-window">
                        {t('goalGame.windowLabel')}: <span className="font-bold text-white">{window_}s</span>
                    </label>
                    <input
                        id="goal-window" type="range" min={data.window_min} max={data.window_max} step={10}
                        value={window_}
                        onChange={(e) => saveWindow(e.target.value)}
                        data-testid="goal-window-slider"
                        className="w-full accent-amber-400"
                    />
                    <p className="text-xs text-white/40">{data.window_min}–{data.window_max}s</p>
                </div>

                <button
                    onClick={() => post('/end')}
                    disabled={busy || !live}
                    data-testid="goal-end-button"
                    className="mt-6 w-full rounded-xl border border-white/20 py-3 font-semibold text-white/80 disabled:opacity-40"
                >
                    {t('goalGame.endButton')}
                </button>

                <p className="mt-8 text-xs text-white/40" data-testid="goal-operator-fanlink">
                    {t('goalGame.fanLink')}: <span className="font-mono">/goal/{data.card_id}</span>
                </p>
            </div>
        </div>
    );
}
